import React, { useContext, useEffect, useState } from 'react';
import { fetchIncomeStatement } from '../api';
import IncomeStatementChart from '../components/IncomeStatementChart';
import IncomeStatementTable from '../components/IncomeStatementTable';
import { StockVisualizationContext } from '../contexts/StockVisualizationContext';

const toChartData = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .map((row) => ({
      fiscalYear: row.fiscalYear,
      revenue: row.revenue ?? null,
      costOfRevenue: row.costOfRevenue ?? null,
      grossProfit: row.grossProfit ?? null,
      netIncome: row.netIncome ?? null,
      ebitda: row.ebitda ?? null
    }))
    .filter((row) => row.fiscalYear)
    .sort((a, b) => Number(a.fiscalYear) - Number(b.fiscalYear));
};

export default function IncomeStatement() {
  const { selectedStock } = useContext(StockVisualizationContext);
  const [incomeStatementRows, setIncomeStatementRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const loadIncomeStatement = async () => {
      if (!selectedStock) {
        setIncomeStatementRows([]);
        setChartData([]);
        setError('');
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const incomeStatement = await fetchIncomeStatement(selectedStock);
        if (isCancelled) return;
        setIncomeStatementRows(Array.isArray(incomeStatement) ? incomeStatement : []);
        setChartData(toChartData(incomeStatement));
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching income statement data:', err);
        setIncomeStatementRows([]);
        setChartData([]);
        setError(`Unable to load income statement data for ${selectedStock}.`);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadIncomeStatement();

    return () => {
      isCancelled = true;
    };
  }, [selectedStock]);

  return (
    <div className="App">
      <div className="main-container">
        <div className="content-area">
          <h2>Income Statement</h2>
          <p>Selected stock: {selectedStock}</p>
          {isLoading ? <p>Loading income statement data...</p> : null}
          {error ? <p>{error}</p> : null}
          {!isLoading && !error ? (
            <>
              <IncomeStatementChart chartData={chartData} selectedStock={selectedStock} />
              <IncomeStatementTable rows={incomeStatementRows} selectedStock={selectedStock} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
